import { type Account } from '@/api/generated/model';
import { AccountHoldingsTable } from '@/components/accounts/AccountHoldingsTable';
import { Button } from '@/components/ui/button';
import { accountSupportsHoldings } from '@/features/accounts/tradingAccountTypes';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency } from '@/lib/utils';
import {
  Building,
  Building2,
  Coins,
  CreditCard,
  Home,
  type LucideIcon,
  Pencil,
  PiggyBank,
  RefreshCw,
  Trash2,
  Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ACCOUNT_ICON: Record<string, LucideIcon> = {
  bank: Building,
  investment: PiggyBank,
  'real-estate': Home,
  crypto: Coins,
  alternative: Building2,
  liability: CreditCard,
};

interface AccountPreviewProps {
  account: Account;
  currency: string;
  onEdit?: (account: Account) => void;
  onRefreshBalance?: (account: Account) => void;
  onDelete?: (account: Account) => void;
}

/** Compact summary of an account, rendered inside the sliding detail aside. */
export const AccountPreview = ({
  account,
  currency,
  onEdit,
  onRefreshBalance,
  onDelete,
}: AccountPreviewProps) => {
  const { t } = useLanguage();
  const Icon = ACCOUNT_ICON[account.type] ?? Wallet;
  const supportsHoldings = accountSupportsHoldings(account);
  const hasActions = Boolean(onEdit || onRefreshBalance || onDelete);

  const rows: Array<{ label: string; value: string }> = [
    { label: t('accounts.details.type'), value: t(`accounts.types.${account.type}`) },
    { label: t('accounts.details.institution'), value: account.institution || '—' },
  ];

  return (
    <div className='space-y-4'>
      <div className='flex items-center gap-3'>
        <div className='bg-fin-accent/15 text-fin-accent rounded-lg p-2.5'>
          <Icon className='size-5' aria-hidden />
        </div>
        <div className='min-w-0'>
          <div className='text-muted-foreground text-xs'>{t('accounts.details.balance')}</div>
          <div className='text-fin-white truncate text-xl font-semibold'>
            {formatCurrency(account.balance, account.currency || currency)}
          </div>
        </div>
      </div>

      <dl className='divide-fin-gray-800 divide-y rounded-lg border'>
        {rows.map((row) => (
          <div key={row.label} className='flex items-center justify-between gap-3 px-3 py-2.5'>
            <dt className='text-muted-foreground text-sm'>{row.label}</dt>
            <dd className='truncate text-sm font-medium'>{row.value}</dd>
          </div>
        ))}
      </dl>

      {supportsHoldings ? (
        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-2'>
            <h3 className='text-fin-white text-sm font-medium'>{t('accounts.tabs.holdings')}</h3>
            <Link
              to={`/accounts/${account.id}/holdings`}
              className='text-fin-accent text-xs font-medium hover:underline'
            >
              {t('investing.viewHoldings')}
            </Link>
          </div>
          <AccountHoldingsTable accountId={account.id} compact />
        </div>
      ) : null}

      {hasActions ? (
        <div className='flex flex-col gap-2'>
          {onEdit ? (
            <Button
              type='button'
              variant='outline'
              className='w-full justify-start'
              onClick={() => onEdit(account)}
            >
              <Pencil className='mr-2 size-3.5' aria-hidden />
              {t('accounts.actions.edit')}
            </Button>
          ) : null}
          {onRefreshBalance ? (
            <Button
              type='button'
              variant='outline'
              className='w-full justify-start'
              onClick={() => onRefreshBalance(account)}
            >
              <RefreshCw className='mr-2 size-3.5' aria-hidden />
              {t('accounts.actions.refreshBalance')}
            </Button>
          ) : null}
          {onDelete ? (
            <Button
              type='button'
              variant='outline'
              className='text-fin-danger hover:text-fin-danger w-full justify-start'
              onClick={() => onDelete(account)}
            >
              <Trash2 className='mr-2 size-3.5' aria-hidden />
              {t('accounts.actions.delete')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AccountPreview;
